// ===================================
// ADMIN COUPONS PAGE - FIXED
// src/pages/AdminCoupons.js
// Phase 6: Complete coupon management interface
// FIXED: Better modal form layout
// ===================================

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './AdminCoupons.css';

const AdminCoupons = () => {
  // State
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    type: '',
    search: '',
    sort: 'created_at',
    order: 'desc'
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Selected coupon
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [usageData, setUsageData] = useState({ usage: [], pagination: {} });
  
  // Reports
  const [reports, setReports] = useState(null);
  const [showReports, setShowReports] = useState(false);
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    minimum_amount: '',
    max_uses: '',
    max_uses_per_user: 1,
    starts_at: '',
    expires_at: '',
    is_active: true
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  // Fetch coupons
  const fetchCoupons = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sort: filters.sort,
        order: filters.order
      });
      
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/coupons/admin?${params}`);
      
      setCoupons(response.data.coupons || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 0
      }));
    } catch (err) {
      console.error('Fetch coupons error:', err);
      setError(err.response?.data?.message || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  // Load coupons on mount and filter change
  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  // Fetch reports
  const fetchReports = async () => {
    try {
      const response = await api.get('/coupons/admin/reports?period=30');
      setReports(response.data);
      setShowReports(true);
    } catch (err) {
      console.error('Fetch reports error:', err);
      alert('Failed to load reports');
    }
  };

  // Generate random code
  const generateCode = async () => {
    try {
      const response = await api.post('/coupons/admin/generate-code', {
        prefix: '',
        length: 8
      });
      setFormData(prev => ({ ...prev, code: response.data.code }));
    } catch (err) {
      console.error('Generate code error:', err);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      minimum_amount: '',
      max_uses: '',
      max_uses_per_user: 1,
      starts_at: '',
      expires_at: '',
      is_active: true
    });
    setFormError(null);
  };

  // Open create modal
  const handleOpenCreate = () => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      starts_at: new Date().toISOString().slice(0, 16)
    }));
    setShowCreateModal(true);
  };

  // Open edit modal
  const handleOpenEdit = (coupon) => {
    setSelectedCoupon(coupon);
    setFormData({
      code: coupon.code,
      name: coupon.name || '',
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      minimum_amount: coupon.minimum_amount ? (coupon.minimum_amount / 100).toFixed(2) : '',
      max_uses: coupon.max_uses || '',
      max_uses_per_user: coupon.max_uses_per_user ?? 1,
      starts_at: coupon.starts_at ? new Date(coupon.starts_at).toISOString().slice(0, 16) : '',
      expires_at: coupon.expires_at ? new Date(coupon.expires_at).toISOString().slice(0, 16) : '',
      is_active: coupon.is_active
    });
    setFormError(null);
    setShowEditModal(true);
  };

  // Create coupon
  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      const payload = {
        code: formData.code.trim().toUpperCase(),
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        minimum_amount: formData.minimum_amount ? parseFloat(formData.minimum_amount) * 100 : 0,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: formData.max_uses_per_user ? parseInt(formData.max_uses_per_user) : null,
        starts_at: formData.starts_at || new Date().toISOString(),
        expires_at: formData.expires_at || null,
        is_active: formData.is_active
      };

      await api.post('/coupons/admin', payload);
      
      setShowCreateModal(false);
      resetForm();
      fetchCoupons();
    } catch (err) {
      console.error('Create coupon error:', err);
      setFormError(err.response?.data?.message || 'Failed to create coupon');
    } finally {
      setFormLoading(false);
    }
  };

  // Update coupon
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedCoupon) return;
    
    setFormLoading(true);
    setFormError(null);

    try {
      const payload = {
        code: formData.code.trim().toUpperCase(),
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        minimum_amount: formData.minimum_amount ? parseFloat(formData.minimum_amount) * 100 : 0,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: formData.max_uses_per_user ? parseInt(formData.max_uses_per_user) : null,
        starts_at: formData.starts_at || null,
        expires_at: formData.expires_at || null,
        is_active: formData.is_active
      };

      await api.put(`/coupons/admin/${selectedCoupon.id}`, payload);
      
      setShowEditModal(false);
      setSelectedCoupon(null);
      resetForm();
      fetchCoupons();
    } catch (err) {
      console.error('Update coupon error:', err);
      setFormError(err.response?.data?.message || 'Failed to update coupon');
    } finally {
      setFormLoading(false);
    }
  };

  // Toggle coupon status
  const handleToggleStatus = async (coupon) => {
    try {
      if (coupon.is_active) {
        await api.delete(`/coupons/admin/${coupon.id}`);
      } else {
        await api.post(`/coupons/admin/${coupon.id}/reactivate`);
      }
      fetchCoupons();
    } catch (err) {
      console.error('Toggle status error:', err);
      alert(err.response?.data?.message || 'Failed to update coupon status');
    }
  };

  // Delete coupon
  const handleDelete = async () => {
    if (!selectedCoupon) return;
    
    try {
      await api.delete(`/coupons/admin/${selectedCoupon.id}?hard=true`);
      setShowDeleteModal(false);
      setSelectedCoupon(null);
      fetchCoupons();
    } catch (err) {
      console.error('Delete coupon error:', err);
      alert(err.response?.data?.message || 'Failed to delete coupon');
    }
  };

  // View usage
  const handleViewUsage = async (coupon) => {
    setSelectedCoupon(coupon);
    try {
      const response = await api.get(`/coupons/admin/${coupon.id}/usage?limit=50`);
      setUsageData(response.data);
      setShowUsageModal(true);
    } catch (err) {
      console.error('Fetch usage error:', err);
      alert('Failed to load usage data');
    }
  };

  // Bulk deactivate
  const handleBulkDeactivate = async () => {
    if (!selectedIds.length) return;
    
    try {
      await Promise.all(
        selectedIds.map(id => api.delete(`/coupons/admin/${id}`))
      );
      setSelectedIds([]);
      fetchCoupons();
    } catch (err) {
      console.error('Bulk deactivate error:', err);
      alert('Failed to deactivate some coupons');
    }
  };

  // Toggle select all
  const handleSelectAll = () => {
    if (selectedIds.length === coupons.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(coupons.map(c => c.id));
    }
  };

  // Toggle single selection
  const handleToggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  // Format currency
  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get status badge
  const getStatusBadge = (coupon) => {
    if (!coupon.is_active) {
      return <span className="status-badge inactive">Inactive</span>;
    }
    if (coupon.is_expired) {
      return <span className="status-badge expired">Expired</span>;
    }
    if (coupon.is_depleted) {
      return <span className="status-badge depleted">Depleted</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  // Close modal helper
  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    resetForm();
  };

  return (
    <div className="admin-coupons-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">🎟️ Coupon Management</h1>
          <p className="page-subtitle">Create and manage discount codes</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={fetchReports}>
            📊 Reports
          </button>
          <button className="btn btn-primary" onClick={handleOpenCreate}>
            + Create Coupon
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <input
            type="text"
            className="filter-input search"
            placeholder="Search codes or names..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />
        </div>
        
        <div className="filter-group">
          <select
            className="filter-select"
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="expired">Expired</option>
            <option value="depleted">Depleted</option>
          </select>
        </div>
        
        <div className="filter-group">
          <select
            className="filter-select"
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
          >
            <option value="">All Types</option>
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={`${filters.sort}-${filters.order}`}
            onChange={(e) => {
              const [sort, order] = e.target.value.split('-');
              setFilters(prev => ({ ...prev, sort, order }));
            }}
          >
            <option value="created_at-desc">Newest First</option>
            <option value="created_at-asc">Oldest First</option>
            <option value="current_uses-desc">Most Used</option>
            <option value="code-asc">Code A-Z</option>
            <option value="expires_at-asc">Expiring Soon</option>
          </select>
        </div>

        {selectedIds.length > 0 && (
          <div className="bulk-actions">
            <span className="selected-count">{selectedIds.length} selected</span>
            <button className="btn btn-sm btn-danger" onClick={handleBulkDeactivate}>
              Deactivate Selected
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="error-banner">
          <span>⚠️</span> {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading coupons...</p>
        </div>
      ) : coupons.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🎟️</span>
          <h3>No Coupons Found</h3>
          <p>Create your first coupon to get started</p>
          <button className="btn btn-primary" onClick={handleOpenCreate}>
            + Create Coupon
          </button>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="coupons-table-wrapper">
            <table className="coupons-table">
              <thead>
                <tr>
                  <th className="col-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === coupons.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Discount</th>
                  <th>Usage</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(coupon => (
                  <tr key={coupon.id} className={!coupon.is_active ? 'inactive-row' : ''}>
                    <td className="col-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(coupon.id)}
                        onChange={() => handleToggleSelect(coupon.id)}
                      />
                    </td>
                    <td className="col-code">
                      <span className="coupon-code">{coupon.code}</span>
                    </td>
                    <td className="col-name">
                      <div className="coupon-name">{coupon.name || '—'}</div>
                      {coupon.description && (
                        <div className="coupon-description">{coupon.description}</div>
                      )}
                    </td>
                    <td className="col-discount">
                      <span className="discount-value">{coupon.discount_display}</span>
                      {coupon.minimum_amount > 0 && (
                        <div className="min-amount">Min: {formatCurrency(coupon.minimum_amount)}</div>
                      )}
                    </td>
                    <td className="col-usage">
                      <span className="usage-count">{coupon.current_uses}</span>
                      {coupon.max_uses && (
                        <span className="usage-max"> / {coupon.max_uses}</span>
                      )}
                      {coupon.remaining_uses !== null && coupon.remaining_uses <= 10 && (
                        <div className="low-uses-warning">⚠️ {coupon.remaining_uses} left</div>
                      )}
                    </td>
                    <td className="col-status">
                      {getStatusBadge(coupon)}
                    </td>
                    <td className="col-expires">
                      {formatDate(coupon.expires_at)}
                    </td>
                    <td className="col-actions">
                      <div className="action-buttons">
                        <button
                          className="action-btn"
                          title="View Usage"
                          onClick={() => handleViewUsage(coupon)}
                        >
                          📊
                        </button>
                        <button
                          className="action-btn"
                          title="Edit"
                          onClick={() => handleOpenEdit(coupon)}
                        >
                          ✏️
                        </button>
                        <button
                          className={`action-btn ${coupon.is_active ? 'deactivate' : 'activate'}`}
                          title={coupon.is_active ? 'Deactivate' : 'Activate'}
                          onClick={() => handleToggleStatus(coupon)}
                        >
                          {coupon.is_active ? '⏸️' : '▶️'}
                        </button>
                        <button
                          className="action-btn delete"
                          title="Delete"
                          onClick={() => {
                            setSelectedCoupon(coupon);
                            setShowDeleteModal(true);
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </span>
              <button
                className="pagination-btn"
                disabled={pagination.page >= pagination.pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* =========================================
          CREATE/EDIT MODAL - IMPROVED LAYOUT
          ========================================= */}
      {(showCreateModal || showEditModal) && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal coupon-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{showCreateModal ? '✨ Create New Coupon' : '✏️ Edit Coupon'}</h2>
              <button className="modal-close" onClick={closeModals}>×</button>
            </div>
            
            <form onSubmit={showCreateModal ? handleCreate : handleUpdate}>
              <div className="modal-body">
                {formError && (
                  <div className="form-error">⚠️ {formError}</div>
                )}

                {/* Section: Basic Info */}
                <div className="form-section">
                  <h3 className="form-section-title">Basic Information</h3>
                  
                  {/* Code with Generate */}
                  <div className="form-group">
                    <label>Coupon Code <span className="required">*</span></label>
                    <div className="code-input-wrapper">
                      <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                        placeholder="e.g. SAVE25, WELCOME50"
                        required
                        maxLength={20}
                        className="code-input"
                      />
                      <button type="button" className="btn btn-generate" onClick={generateCode}>
                        🎲 Generate
                      </button>
                    </div>
                    <span className="form-hint">Customers will enter this code at checkout</span>
                  </div>

                  {/* Name */}
                  <div className="form-group">
                    <label>Display Name <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Summer Sale, Welcome Discount"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div className="form-group">
                    <label>Description <span className="optional">(optional)</span></label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Internal notes about this coupon..."
                      rows={2}
                    />
                  </div>
                </div>

                {/* Section: Discount */}
                <div className="form-section">
                  <h3 className="form-section-title">Discount Settings</h3>
                  
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Discount Type <span className="required">*</span></label>
                      <select
                        value={formData.discount_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, discount_type: e.target.value }))}
                      >
                        <option value="percentage">Percentage Off (%)</option>
                        <option value="fixed">Fixed Amount ($)</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>
                        Discount Value <span className="required">*</span>
                        <span className="label-suffix">
                          {formData.discount_type === 'percentage' ? '(%)' : '($)'}
                        </span>
                      </label>
                      <input
                        type="number"
                        value={formData.discount_value}
                        onChange={(e) => setFormData(prev => ({ ...prev, discount_value: e.target.value }))}
                        placeholder={formData.discount_type === 'percentage' ? 'e.g. 25' : 'e.g. 5.00'}
                        required
                        min="0"
                        max={formData.discount_type === 'percentage' ? '100' : '9999'}
                        step={formData.discount_type === 'percentage' ? '1' : '0.01'}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Minimum Purchase Amount <span className="optional">(optional)</span></label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        value={formData.minimum_amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, minimum_amount: e.target.value }))}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <span className="form-hint">Leave empty for no minimum purchase requirement</span>
                  </div>
                </div>

                {/* Section: Usage Limits */}
                <div className="form-section">
                  <h3 className="form-section-title">Usage Limits</h3>
                  
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Total Uses Allowed <span className="optional">(optional)</span></label>
                      <input
                        type="number"
                        value={formData.max_uses}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_uses: e.target.value }))}
                        placeholder="Unlimited"
                        min="1"
                      />
                      <span className="form-hint">Leave empty for unlimited</span>
                    </div>
                    
                    <div className="form-group">
                      <label>Uses Per Customer</label>
                      <input
                        type="number"
                        value={formData.max_uses_per_user}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_uses_per_user: e.target.value }))}
                        placeholder="1"
                        min="1"
                      />
                      <span className="form-hint">How many times each user can use this</span>
                    </div>
                  </div>
                </div>

                {/* Section: Validity Period */}
                <div className="form-section">
                  <h3 className="form-section-title">Validity Period</h3>
                  
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="datetime-local"
                        value={formData.starts_at}
                        onChange={(e) => setFormData(prev => ({ ...prev, starts_at: e.target.value }))}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Expiry Date <span className="optional">(optional)</span></label>
                      <input
                        type="datetime-local"
                        value={formData.expires_at}
                        onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
                      />
                      <span className="form-hint">Leave empty for no expiry</span>
                    </div>
                  </div>

                  {/* Active Toggle */}
                  <div className="form-group">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                      <span className="toggle-switch"></span>
                      <span className="toggle-text">
                        {formData.is_active ? '✅ Active - Coupon can be used' : '⏸️ Inactive - Coupon is disabled'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModals}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? '⏳ Saving...' : (showCreateModal ? '✨ Create Coupon' : '💾 Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Usage Modal */}
      {showUsageModal && selectedCoupon && (
        <div className="modal-overlay" onClick={() => setShowUsageModal(false)}>
          <div className="modal usage-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📊 Usage History: <span className="coupon-code">{selectedCoupon.code}</span></h2>
              <button className="modal-close" onClick={() => setShowUsageModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="usage-summary">
                <div className="summary-stat">
                  <span className="stat-value">{usageData.coupon?.total_uses || 0}</span>
                  <span className="stat-label">Total Uses</span>
                </div>
                <div className="summary-stat">
                  <span className="stat-value">{selectedCoupon.max_uses || '∞'}</span>
                  <span className="stat-label">Max Uses</span>
                </div>
              </div>

              {usageData.usage?.length > 0 ? (
                <table className="usage-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Original</th>
                      <th>Discount</th>
                      <th>Final</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageData.usage.map((use, idx) => (
                      <tr key={idx}>
                        <td>
                          <div className="user-cell">
                            <span className="user-email">{use.user_email || 'Unknown'}</span>
                            {use.user_name && <span className="user-name">{use.user_name}</span>}
                          </div>
                        </td>
                        <td>{formatCurrency(use.original_amount)}</td>
                        <td className="discount-cell">-{formatCurrency(use.discount_amount)}</td>
                        <td>{formatCurrency(use.final_amount)}</td>
                        <td>{formatDate(use.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-usage">
                  <p>No usage history yet</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUsageModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCoupon && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal delete-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🗑️ Delete Coupon</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="delete-warning">
                <span className="warning-icon">⚠️</span>
                <p>Are you sure you want to delete <strong className="coupon-code">{selectedCoupon.code}</strong>?</p>
                <p className="warning-note">
                  {selectedCoupon.current_uses > 0 
                    ? `This coupon has been used ${selectedCoupon.current_uses} time(s). It will be deactivated instead of deleted.`
                    : 'This action cannot be undone.'
                  }
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                {selectedCoupon.current_uses > 0 ? '⏸️ Deactivate' : '🗑️ Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reports Modal */}
      {showReports && reports && (
        <div className="modal-overlay" onClick={() => setShowReports(false)}>
          <div className="modal reports-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📊 Coupon Analytics</h2>
              <button className="modal-close" onClick={() => setShowReports(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="reports-grid">
                <div className="report-card">
                  <span className="report-value">{reports.summary?.total_coupons || 0}</span>
                  <span className="report-label">Total Coupons</span>
                </div>
                <div className="report-card">
                  <span className="report-value">{reports.summary?.active_coupons || 0}</span>
                  <span className="report-label">Active</span>
                </div>
                <div className="report-card">
                  <span className="report-value">{reports.period_stats?.usage_count || 0}</span>
                  <span className="report-label">Uses (30 days)</span>
                </div>
                <div className="report-card highlight">
                  <span className="report-value">{reports.period_stats?.total_discounts_display || '$0.00'}</span>
                  <span className="report-label">Total Discounts</span>
                </div>
              </div>

              {reports.top_coupons?.length > 0 && (
                <div className="reports-section">
                  <h3>🏆 Top Performing Coupons</h3>
                  <table className="mini-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Uses</th>
                        <th>Total Discount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.top_coupons.slice(0, 5).map((c, i) => (
                        <tr key={i}>
                          <td><span className="coupon-code">{c.code}</span></td>
                          <td>{c.uses}</td>
                          <td>{c.total_discount_display}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {reports.expiring_soon?.length > 0 && (
                <div className="reports-section">
                  <h3>⏰ Expiring Soon</h3>
                  <div className="expiring-list">
                    {reports.expiring_soon.map((c, i) => (
                      <div key={i} className="expiring-item">
                        <span className="coupon-code">{c.code}</span>
                        <span className="expires-date">{formatDate(c.expires_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowReports(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;